import type { ReactNode, Ref, RefObject } from "react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router";

import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import NextButton from "./NextButton";

export interface IInfiniteScollList<T = unknown> {
  /**
   * @description fetch data
   */
  fetchData?: ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => Promise<T[]>;
  /**
   * @description initial data
   */
  initialData: T[];
  /**
   * @description render loading component
   */
  renderLoadingMore?: (loading: boolean, hasMore: boolean) => ReactNode;
  /**
   * @description render item component
   */
  renderItem?: (record: T, index?: number) => ReactNode;
  /**
   * @description page size
   * @default 10
   */
  pageSize?: number;
  /**
   * @description container ref, default document.documentElement
   */
  containerRef?: RefObject<HTMLDivElement>;
  /**
   * @description unique key, default is id
   */
  uniqueKey?: string;
  /**
   *  @description className, default ''
   */
  className?: string;
  /**
   * The pixel threshold to the bottom for the scrolling to load, default 20
   */
  threshold?: number;
  /**
   * @description trigger when no more data
   */
  onEnd?: () => void;
  /**
   * @description trigger when data changed
   */
  onChange?: (data: T[]) => void;
}

export interface IInfiniteScollListRef<T = unknown> {
  /** @deprecated */
  getDataList?: () => T[];
  loadMore?: () => Promise<unknown>;
}

/**
 * InfiniteScollList
 */
function InfiniteScollList<T = unknown>(
  props: IInfiniteScollList<T>,
  ref: Ref<IInfiniteScollListRef>,
) {
  const {
    threshold = 20,
    renderLoadingMore,
    initialData = [],
    renderItem,
    className = "",
    fetchData,
    pageSize = 12,
    containerRef,
    uniqueKey = "id",
    onEnd,
    onChange,
  } = props;
  const [_data, setData] = useState(initialData);
  const [_pagination, setPagination] = useState({ page: 1, pageSize });
  const [_hasMore, setHasMore] = useState(true);
  const [_loading, setLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const page = searchParams.get("page");
  const isInfiniteScrollDisabled = !!page;

  useEffect(() => {
    onChange?.(_data);
  }, [_data, onChange]);

  // update page size when pageSize changed
  useEffect(() => {
    setPagination((p) => ({ ...p, pageSize }));
  }, [pageSize]);

  const loadMore = useCallback(
    async (paginationProp?: Partial<typeof _pagination>) => {
      if (!_hasMore || _loading) return [];
      setLoading(true);

      try {
        const params = {
          page: paginationProp?.page || _pagination.page + 1,
          pageSize: paginationProp?.pageSize || _pagination.pageSize,
        };

        if (params.page == 1) {
          // setData(newData || []);
          return;
        }

        const newData = await fetchData?.(params);

        // remove duplicated data, add uniqueKey prop
        setData((prevData) => {
          // @ts-expect-error id is not in T
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          const idSet = new Set(prevData.map((item) => item[uniqueKey] || ""));
          // @ts-expect-error id is not in T
          const _newData = newData?.filter((i) => !idSet.has(i[uniqueKey]));

          return [...prevData, ...(_newData || [])];
        });
        setPagination((i) => ({ ...i, ...params }));

        if (newData?.length === 0 || (newData?.length || 0) < params.pageSize) {
          setHasMore(false);
          onEnd?.();
        }
      } catch (error) {
        console.log(error);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [
      _hasMore,
      _loading,
      _pagination,
      uniqueKey,
      setLoading,
      setData,
      setPagination,
      setHasMore,
      fetchData,
      onEnd,
    ],
  );

  useEffect(() => {
    const targetPage = parseInt(page || "1");

    if (isInfiniteScrollDisabled) {
      setData([]);
    }

    void loadMore({ page: targetPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMoreRef = useRef(loadMore);

  useEffect(() => {
    loadMoreRef.current = loadMore;
  }, [loadMore]);

  useImperativeHandle(ref, () => {
    return {
      getDataList: () => _data,
      loadMore: () => loadMoreRef.current(),
    };
  }, [_data]);

  useInfiniteScroll({
    containerRef,
    threshold,
    disabled: isInfiniteScrollDisabled,
    onLoadMore: () => loadMoreRef.current(),
    nextButtonSelector: "#next-button",
  });

  const loadingElement = renderLoadingMore?.(_loading, _hasMore);

  if (!_data.length) return loadingElement;

  return (
    <>
      <div className={className}>
        {_data.map((item, index) => renderItem?.(item, index))}
      </div>

      {/* test */}
      {/* <div className=" fixed right-0 top-1/2 w-[400px] h-[400px] bg-gray-400 z-50 ">
        <div>Dashboar to debug</div>
        <div>loading: {_loading.toString()}</div>
        <div>hasMore: {_hasMore.toString()}</div>
        <div>articles length: {_data.length}</div>
        <div>
          pagination: {_pagination.page},{_pagination.pageSize}
        </div>
      </div> */}

      {loadingElement}

      <NextButton
        currentPage={_pagination.page}
        loading={_loading}
        onNext={() => {
          void loadMoreRef.current({ page: _pagination.page + 1 });
        }}
        hasMore={_hasMore}
        text="Next Page"
      />
    </>
  );
}

export default forwardRef(InfiniteScollList) as <T>(
  p: IInfiniteScollList<T> & { ref?: Ref<IInfiniteScollListRef> },
) => JSX.Element;
